import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { api } from '../../utils/api';
import { toast } from 'sonner';
import { Palette, Eye, Upload } from 'lucide-react';

const BrandingEditor = () => {
  const [loading, setLoading] = useState(false);
  const agent = JSON.parse(localStorage.getItem('agent') || '{}');
  const [branding, setBranding] = useState({
    company_name: 'Valeur Immo',
    logo_url: '',
    primary_color: '#1E3A8A',
    secondary_color: '#E8EEFF',
    accent_color: '#0F766E',
    ring_color: '#1E3A8A'
  });

  useEffect(() => {
    const loadBranding = async () => {
      try {
        const data = await api.getBranding(agent.slug);
        if (data.branding) {
          setBranding(prev => ({ ...prev, ...data.branding }));
        }
      } catch (error) {
        console.error('Failed to load branding:', error);
      }
    };
    if (agent.slug) loadBranding();
  }, [agent.slug]);

  const handleSave = async () => {
    setLoading(true);
    try {
      await api.updateBranding(agent.slug, branding);
      toast.success('Branding mis à jour', {
        description: 'Vos paramètres de marque ont été enregistrés.'
      });
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => {
    setBranding(prev => ({ ...prev, [field]: value }));
  };

  const previewStyles = {
    '--primary': hexToHSL(branding.primary_color),
    '--secondary': hexToHSL(branding.secondary_color),
    '--accent': hexToHSL(branding.accent_color),
    '--ring': hexToHSL(branding.ring_color)
  };

  // Simple hex to HSL converter for preview
  function hexToHSL(hex) {
    hex = hex.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16) / 255;
    const g = parseInt(hex.substring(2, 4), 16) / 255;
    const b = parseInt(hex.substring(4, 6), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;

    if (max === min) {
      h = s = 0;
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
        case g: h = ((b - r) / d + 2) / 6; break;
        case b: h = ((r - g) / d + 4) / 6; break;
        default: h = 0;
      }
    }

    return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
  }

  return (
    <div className="space-y-6 max-w-6xl">
      <div>
        <h1 className="text-3xl font-bold mb-2">Marque Blanche (Branding)</h1>
        <p className="text-muted-foreground">
          Personnalisez l'apparence du backoffice et de votre landing page
        </p>
      </div>

      <Alert>
        <Palette className="h-4 w-4" />
        <AlertDescription>
          <strong>Marque blanche activée :</strong> Personnalisez le nom de votre entreprise, le logo et les couleurs. 
          Ces paramètres s'appliqueront à votre backoffice et à votre page landing publique.
        </AlertDescription>
      </Alert>

      <Tabs defaultValue="identity" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="identity">Identité</TabsTrigger>
          <TabsTrigger value="colors">Couleurs</TabsTrigger>
          <TabsTrigger value="preview">Aperçu</TabsTrigger>
        </TabsList>

        {/* Identity Tab */}
        <TabsContent value="identity" className="space-y-6">
          <Card className="p-6 space-y-6">
            <div>
              <Label htmlFor="company_name">Nom de l'entreprise</Label>
              <Input
                id="company_name"
                value={branding.company_name}
                onChange={(e) => updateField('company_name', e.target.value)}
                placeholder="Valeur Immo"
                data-testid="company-name-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Affiché dans le backoffice et sur votre page publique
              </p>
            </div>

            <div>
              <Label htmlFor="logo_url">URL du logo</Label>
              <div className="flex gap-2">
                <Input
                  id="logo_url"
                  value={branding.logo_url}
                  onChange={(e) => updateField('logo_url', e.target.value)}
                  placeholder="https://votre-site.ch/logo.png"
                  data-testid="logo-url-input"
                />
                <Button variant="outline" size="icon">
                  <Upload className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Format recommandé: PNG transparent, 200x50px minimum
              </p>
              
              {branding.logo_url && (
                <div className="mt-4 p-4 bg-muted rounded-lg">
                  <p className="text-xs font-medium mb-2">Aperçu du logo:</p>
                  <img 
                    src={branding.logo_url} 
                    alt="Logo preview" 
                    className="h-12 object-contain"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                </div>
              )}
            </div>
          </Card>
        </TabsContent>

        {/* Colors Tab */}
        <TabsContent value="colors" className="space-y-6">
          <Card className="p-6 space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="primary_color">Couleur primaire</Label>
                <div className="flex gap-2">
                  <Input
                    id="primary_color"
                    type="color"
                    value={branding.primary_color}
                    onChange={(e) => updateField('primary_color', e.target.value)}
                    className="w-20 h-10"
                    data-testid="primary-color-input"
                  />
                  <Input
                    type="text"
                    value={branding.primary_color}
                    onChange={(e) => updateField('primary_color', e.target.value)}
                    placeholder="#1E3A8A"
                    className="flex-1"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Boutons principaux, liens, éléments d'action
                </p>
              </div>

              <div>
                <Label htmlFor="secondary_color">Couleur secondaire</Label>
                <div className="flex gap-2">
                  <Input
                    id="secondary_color"
                    type="color"
                    value={branding.secondary_color}
                    onChange={(e) => updateField('secondary_color', e.target.value)}
                    className="w-20 h-10"
                    data-testid="secondary-color-input"
                  />
                  <Input
                    type="text"
                    value={branding.secondary_color}
                    onChange={(e) => updateField('secondary_color', e.target.value)}
                    placeholder="#E8EEFF"
                    className="flex-1"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Boutons secondaires, badges, arrière-plans subtils
                </p>
              </div>

              <div>
                <Label htmlFor="accent_color">Couleur d'accent</Label>
                <div className="flex gap-2">
                  <Input
                    id="accent_color"
                    type="color"
                    value={branding.accent_color}
                    onChange={(e) => updateField('accent_color', e.target.value)}
                    className="w-20 h-10"
                    data-testid="accent-color-input"
                  />
                  <Input
                    type="text"
                    value={branding.accent_color}
                    onChange={(e) => updateField('accent_color', e.target.value)}
                    placeholder="#0F766E"
                    className="flex-1"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Éléments spéciaux, highlights, notifications positives
                </p>
              </div>

              <div>
                <Label htmlFor="ring_color">Couleur de focus</Label>
                <div className="flex gap-2">
                  <Input
                    id="ring_color"
                    type="color"
                    value={branding.ring_color}
                    onChange={(e) => updateField('ring_color', e.target.value)}
                    className="w-20 h-10"
                    data-testid="ring-color-input"
                  />
                  <Input
                    type="text"
                    value={branding.ring_color}
                    onChange={(e) => updateField('ring_color', e.target.value)}
                    placeholder="#1E3A8A"
                    className="flex-1"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Bordure de focus pour l'accessibilité (navigation clavier)
                </p>
              </div>
            </div>

            <div className="pt-4 border-t">
              <h4 className="font-semibold mb-3">Palette de couleurs rapide</h4>
              <div className="grid grid-cols-5 gap-2">
                {[
                  { name: 'Bleu suisse', primary: '#1E3A8A', secondary: '#E8EEFF', accent: '#0F766E' },
                  { name: 'Vert nature', primary: '#059669', secondary: '#D1FAE5', accent: '#14B8A6' },
                  { name: 'Rouge énergie', primary: '#DC2626', secondary: '#FEE2E2', accent: '#F59E0B' },
                  { name: 'Violet premium', primary: '#7C3AED', secondary: '#EDE9FE', accent: '#EC4899' },
                  { name: 'Gris élégant', primary: '#374151', secondary: '#F3F4F6', accent: '#6366F1' }
                ].map((palette, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      updateField('primary_color', palette.primary);
                      updateField('secondary_color', palette.secondary);
                      updateField('accent_color', palette.accent);
                    }}
                    className="p-3 border rounded-lg hover:border-primary transition-colors"
                    title={palette.name}
                  >
                    <div className="flex flex-col gap-1">
                      <div className="h-6 rounded" style={{ backgroundColor: palette.primary }} />
                      <div className="h-3 rounded" style={{ backgroundColor: palette.secondary }} />
                      <div className="h-3 rounded" style={{ backgroundColor: palette.accent }} />
                    </div>
                    <p className="text-[10px] mt-1 text-center">{palette.name}</p>
                  </button>
                ))}
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* Preview Tab */}
        <TabsContent value="preview" className="space-y-6">
          <Card className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Eye className="w-5 h-5" />
              <h3 className="font-semibold">Aperçu du branding</h3>
            </div>

            <div style={previewStyles} className="space-y-6">
              {/* Preview Header */}
              <div className="p-6 border rounded-lg bg-primary text-primary-foreground">
                <div className="flex items-center gap-4">
                  {branding.logo_url ? (
                    <img src={branding.logo_url} alt="Logo" className="h-8 object-contain" />
                  ) : (
                    <div className="w-12 h-12 rounded bg-primary-foreground/20" />
                  )}
                  <div>
                    <h3 className="font-bold text-lg">{branding.company_name}</h3>
                    <p className="text-sm opacity-90">Votre backoffice personnalisé</p>
                  </div>
                </div>
              </div>

              {/* Preview Buttons */}
              <div className="flex flex-wrap gap-3">
                <Button style={{ backgroundColor: `hsl(${previewStyles['--primary']})` }}>
                  Bouton primaire
                </Button>
                <Button variant="secondary" style={{ backgroundColor: `hsl(${previewStyles['--secondary']})` }}>
                  Bouton secondaire
                </Button>
                <Badge style={{ backgroundColor: `hsl(${previewStyles['--accent']})`, color: 'white' }}>
                  Badge accent
                </Badge>
              </div>

              {/* Preview Card */}
              <div className="p-4 border-2 rounded-lg" style={{ borderColor: `hsl(${previewStyles['--primary']})` }}>
                <h4 className="font-semibold mb-2">Carte avec bordure primaire</h4>
                <p className="text-sm text-muted-foreground">
                  Les couleurs de votre marque s'appliquent automatiquement partout dans le backoffice.
                </p>
              </div>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      <div className="flex gap-4">
        <Button onClick={handleSave} disabled={loading} data-testid="save-branding-button">
          {loading ? 'Enregistrement...' : 'Enregistrer le branding'}
        </Button>
        <Button 
          variant="outline" 
          onClick={() => window.open(`/a/${agent.slug}`, '_blank')}
        >
          Voir ma page publique
        </Button>
      </div>
    </div>
  );
};

export default BrandingEditor;
